import GeneralSetupTab from "../../components/GeneralSetupTab";
import React, { useEffect, useState } from "react";
import CenterWrapper from "../../components/CenterWrapper";
import api from "../../api";
import AddTeamsModal from "./AddTeamsModal";
import EditTeamMemberModal from "./EditTeamMemberModal";
import TeamMemberStatusUpdateModal from "./TeamMemberStatusUpdateModal";
import PrimaryButton from "../../components/PrimaryButton";
import SectionHeader from "../../components/SectionHeader";
import {
  CheckCircleIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { capitalize } from "lodash";

const AddTeam = () => {
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [isEditUserStatusModalOpen, setIsEditUserStatusModalOpen] =
    useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [editUserData, setEditUserData] = useState({});

  const fetchTeam = async () => {
    try {
      const response = await api.get(`/api/team`);
      const team = response.data;

      setTeamMembers(team);
    } catch (err) {
      alert(JSON.stringify(err));
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const handleAddUser = () => setIsNewUserModalOpen(true);

  const handleEditUser = userObj => {
    setEditUserData({
      email: userObj.email,
      role: userObj.role,
      active: userObj.active,
      id: userObj.id,
    });
    setIsEditUserModalOpen(true);
  };

  const handleUserStatus = userObj => {
    setEditUserData({
      email: userObj.email,
      role: userObj.role,
      active: userObj.active,
      id: userObj.id,
    });
    setIsEditUserStatusModalOpen(true);
  };

  return (
    <CenterWrapper>
      <GeneralSetupTab
        title={"Account Settings"}
        subtitle={"Update your company settings, team and billing."}
        pageType={"settings"}
        activeTab={"add_team"}
      />

      <div className="bg-white mb-8">
        <SectionHeader
          title="Team members"
          description="Manage your team members and accounts permissions here."
        >
          <PrimaryButton
            type="button"
            onClick={handleAddUser}
            startIcon={<PlusIcon className="size-6" />}
          >
            Add user
          </PrimaryButton>
        </SectionHeader>

        <div className="mt-10 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="text-slate-500 text-xs font-semibold leading-none">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left  sm:pl-6">
                  Name
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left  sm:table-cell"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left  sm:table-cell"
                >
                  Email address
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left  sm:table-cell"
                >
                  Role
                </th>
                <th
                  scope="col"
                  className="relative py-3.5 pl-3 pr-4 text-right sm:pr-6"
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="text-gray-600 text-sm">
              {teamMembers.map(person => (
                <tr key={person.id}>
                  <td className="whitespace-nowrap py-5 pl-4 pr-3 sm:pl-0">
                    <div className="flex items-center">
                      <div className="ml-4 flex-shrink-0">
                        {person.picture_url ? (
                          <img
                            alt="avatar"
                            src={person.picture_url}
                            className="size-11 rounded-full"
                          />
                        ) : (
                          <div className="size-11 rounded-full flex items-center justify-center border bg-gray-100 text-base font-semibold leading-normal text-gray-500">
                            {(person.first_name?.[0] || "").toUpperCase()}
                            {(person.last_name?.[0] || "").toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="leading-tight text-sm font-medium text-gray-900">
                          {person.first_name} {person.last_name}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-3 py-5">
                    {person.active ? (
                      <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                        <svg
                          viewBox="0 0 6 6"
                          aria-hidden="true"
                          className="size-1.5 fill-green-500"
                        >
                          <circle r={3} cx={3} cy={3} />
                        </svg>
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                        <svg
                          viewBox="0 0 6 6"
                          aria-hidden="true"
                          className="size-1.5 fill-red-500"
                        >
                          <circle r={3} cx={3} cy={3} />
                        </svg>
                        Inactive
                      </span>
                    )}
                  </td>

                  <td className="whitespace-nowrap px-3 py-5">
                    {person.email}
                  </td>

                  <td className="relative whitespace-nowrap py-5 pl-3 pr-4 font-medium sm:pr-0">
                    {capitalize(person.role)}
                  </td>

                  {person.role !== "owner" && (
                    <td className="whitespace-nowrap py-6 pl-3 pr-4 text-right sm:pr-6">
                      <div className="inline-flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => handleUserStatus(person)}
                        >
                          {person.active ? (
                            <TrashIcon className="size-5" />
                          ) : (
                            <CheckCircleIcon className="size-5" />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => handleEditUser(person)}
                        >
                          <PencilIcon className="size-5" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AddTeamsModal
        isOpen={isNewUserModalOpen}
        onClose={() => setIsNewUserModalOpen(false)}
        fetchTeam={fetchTeam}
        onSuccess={() => {
          setIsNewUserModalOpen(false);
        }}
        api={api}
      />

      <EditTeamMemberModal
        isOpen={isEditUserModalOpen}
        fetchTeam={fetchTeam}
        onSuccess={() => {
          setIsEditUserModalOpen(false);
          setEditUserData({});
        }}
        onClose={() => {
          setIsEditUserModalOpen(false);
          setEditUserData({});
        }}
        editUserData={editUserData}
        api={api}
      />

      <TeamMemberStatusUpdateModal
        isOpen={isEditUserStatusModalOpen}
        fetchTeam={fetchTeam}
        onSuccess={() => {
          setIsEditUserStatusModalOpen(false);
          setEditUserData({});
        }}
        onClose={() => {
          setIsEditUserStatusModalOpen(false);
          setEditUserData({});
        }}
        editUserData={editUserData}
        api={api}
      />
    </CenterWrapper>
  );
};

export default AddTeam;
